import React, { useState, useEffect, useRef } from 'react';
import './home.css';
import { supabase } from '../supabaseClient';
import DashboardLayout from '../components/layout/Dashboardlayout.jsx';
import { gsap } from 'gsap';
import { DrawSVGPlugin } from 'gsap/DrawSVGPlugin';
import Loader from '../components/layout/preloader';

gsap.registerPlugin(DrawSVGPlugin);

const chartData = [
  { day: 'Mon', value: 11000 },
  { day: 'Tue', value: 18000 },
  { day: 'Wed', value: 24000 },
  { day: 'Thu', value: 31000 },
  { day: 'Fri', value: 44000 },
  { day: 'Sat', value: 52000 },
  { day: 'Sun', value: 37000 },
];

const Home = () => {
  const [bookings, setBookings] = useState([]);

  const rootRef = useRef(null);
  const headerRef = useRef(null);
  const statCardsRef = useRef([]);
  const chartCardRef = useRef(null);
  const tableCardRef = useRef(null);
  const chartPathRef = useRef(null);
  const chartDotsRef = useRef([]);
  const tableRowsRef = useRef([]);

  useEffect(() => {
    const getBookings = async () => {
      const { data: bookingData } = await supabase
        .from('booking')
        .select('user_name, total_paid, payment_status, quantity, ticket_tier_id, exhibitor_id');

      if (!bookingData || bookingData.length === 0) return;

      const { data: ticketTiers } = await supabase
        .from('ticket_tiers')
        .select('id, tier_name_en');

      const { data: exhibitors } = await supabase
        .from('exhibitors')
        .select('id, name');

      const merged = bookingData.map((b) => ({
        ...b,
        tier_name: (ticketTiers || []).find((t) => t.id === b.ticket_tier_id)?.tier_name_en || '—',
        exhibitor_name: (exhibitors || []).find((e) => e.id === b.exhibitor_id)?.name || '—',
      }));

      setBookings(merged);
    };

    getBookings();
  }, []);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: 'power3.out' }, delay: 4 });

      tl.fromTo(
        headerRef.current,
        { opacity: 0, y: -28 },
        { opacity: 1, y: 0, duration: 0.7 }
      );

      tl.fromTo(
        statCardsRef.current,
        { opacity: 0, y: 30, scale: 0.96 },
        { opacity: 1, y: 0, scale: 1, duration: 0.55, stagger: 0.1 },
        '-=0.3'
      );

      tl.fromTo(
        chartCardRef.current,
        { opacity: 0, y: 24 },
        { opacity: 1, y: 0, duration: 0.6 },
        '-=0.2'
      );

      tl.fromTo(
        tableCardRef.current,
        { opacity: 0, y: 24 },
        { opacity: 1, y: 0, duration: 0.6 },
        '-=0.3'
      );

      if (chartPathRef.current) {
        const pathLength = chartPathRef.current.getTotalLength();
        gsap.set(chartPathRef.current, {
          strokeDasharray: pathLength,
          strokeDashoffset: pathLength,
        });
        tl.to(
          chartPathRef.current,
          { strokeDashoffset: 0, duration: 1.4, ease: 'power2.inOut' },
          '-=0.4'
        );
      }

      if (chartDotsRef.current.length) {
        tl.fromTo(
          chartDotsRef.current,
          { opacity: 0, scale: 0, transformOrigin: 'center center' },
          { opacity: 1, scale: 1, duration: 0.35, stagger: 0.08, ease: 'back.out(2)' },
          '-=0.8'
        );
      }

      statCardsRef.current.forEach((card) => {
        if (!card) return;
        card.addEventListener('mouseenter', () => {
          gsap.to(card, { y: -4, duration: 0.25, ease: 'power2.out' });
        });
        card.addEventListener('mouseleave', () => {
          gsap.to(card, { y: 0, duration: 0.3, ease: 'power2.inOut' });
        });
      });
    }, rootRef);

    return () => ctx.revert();
  }, []);

  useEffect(() => {
    if (tableRowsRef.current.length === 0) return;
    gsap.fromTo(
      tableRowsRef.current,
      { opacity: 0, x: -16 },
      { opacity: 1, x: 0, duration: 0.4, stagger: 0.06, ease: 'power2.out', delay: 4.2 }
    );
  }, [bookings]);

  const width = 660;
  const height = 200;
  const paddingLeft = 55;
  const paddingRight = 20;
  const paddingTop = 16;
  const paddingBottom = 30;
  const maxVal = 60000;
  const yLabels = [0, 15000, 30000, 45000, 60000];
  const innerW = width - paddingLeft - paddingRight;
  const innerH = height - paddingTop - paddingBottom;
  const toX = (i) => paddingLeft + (i / (chartData.length - 1)) * innerW;
  const toY = (v) => paddingTop + innerH - (v / maxVal) * innerH;
  const pathD = chartData.map((d, i) => `${i === 0 ? 'M' : 'L'} ${toX(i)} ${toY(d.value)}`).join(' ');

  return (
    <>
      <Loader />
      <DashboardLayout activePath="/dashboard">
        <div className="hm-root" ref={rootRef}>

          <div className="hm-page-header" ref={headerRef}>
            <h1 className="hm-page-title">ANALYTICS DASHBOARD</h1>
            <p className="hm-page-subtitle">Real-time overview of event performance and metrics</p>
          </div>

          <div className="hm-stats-grid">
            <div className="hm-stat-card" ref={(el) => (statCardsRef.current[0] = el)}>
              <div className="hm-stat-card__top">
                <span className="hm-stat-card__label">TOTAL REVENUE</span>
                <span className="hm-stat-card__icon">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="1" x2="12" y2="23"/>
                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                  </svg>
                </span>
              </div>
              <div className="hm-stat-card__value">EGP 1,245,000</div>
              <div className="hm-stat-card__change hm-stat-card__change--up">+12.5% from last month</div>
            </div>

            <div className="hm-stat-card" ref={(el) => (statCardsRef.current[1] = el)}>
              <div className="hm-stat-card__top">
                <span className="hm-stat-card__label">TOTAL ATTENDEES</span>
                <span className="hm-stat-card__icon">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                </span>
              </div>
              <div className="hm-stat-card__value">3,847</div>
              <div className="hm-stat-card__change hm-stat-card__change--up">+18.2% from last month</div>
            </div>

            <div className="hm-stat-card" ref={(el) => (statCardsRef.current[2] = el)}>
              <div className="hm-stat-card__top">
                <span className="hm-stat-card__label">ACTIVE EXHIBITORS</span>
                <span className="hm-stat-card__icon">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="7" width="20" height="14" rx="2"/>
                    <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
                    <line x1="12" y1="12" x2="12" y2="16"/>
                    <line x1="10" y1="14" x2="14" y2="14"/>
                  </svg>
                </span>
              </div>
              <div className="hm-stat-card__value">156</div>
              <div className="hm-stat-card__change hm-stat-card__change--up">12 new this week</div>
            </div>

            <div className="hm-stat-card" ref={(el) => (statCardsRef.current[3] = el)}>
              <div className="hm-stat-card__top">
                <span className="hm-stat-card__label">TOP CATEGORY</span>
                <span className="hm-stat-card__icon">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
                    <polyline points="17 6 23 6 23 12"/>
                  </svg>
                </span>
              </div>
              <div className="hm-stat-card__value hm-stat-card__value--lg">Architecture</div>
              <div className="hm-stat-card__change hm-stat-card__change--up">42% of total</div>
            </div>
          </div>

          <div className="hm-chart-card" ref={chartCardRef}>
            <h2 className="hm-section-title">TICKET SALES PER DAY</h2>
            <div className="hm-chart-wrap">
              <svg viewBox={`0 0 ${width} ${height}`} width="100%" height="100%" preserveAspectRatio="none">
                {yLabels.map((label) => (
                  <g key={label}>
                    <line x1={paddingLeft} y1={toY(label)} x2={width - paddingRight} y2={toY(label)} stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
                    <text x={paddingLeft - 8} y={toY(label) + 4} textAnchor="end" fill="rgba(232,226,217,0.45)" fontSize="11" fontFamily="Jost, sans-serif">
                      {label === 0 ? '0' : `${label / 1000}k`}
                    </text>
                  </g>
                ))}
                {chartData.map((d, i) => (
                  <text key={d.day} x={toX(i)} y={height - 6} textAnchor="middle" fill="rgba(232,226,217,0.45)" fontSize="11" fontFamily="Jost, sans-serif">
                    {d.day}
                  </text>
                ))}
                <path
                  ref={chartPathRef}
                  d={pathD}
                  fill="none"
                  stroke="#FF1E1E"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                {chartData.map((d, i) => (
                  <circle
                    key={i}
                    ref={(el) => (chartDotsRef.current[i] = el)}
                    cx={toX(i)}
                    cy={toY(d.value)}
                    r="4.5"
                    fill="#FF1E1E"
                  />
                ))}
              </svg>
            </div>
          </div>

          <div className="hm-table-card" ref={tableCardRef}>
            <h2 className="hm-section-title">LATEST BOOKINGS</h2>
            <div className="hm-table-wrap">
              <table className="hm-table">
                <thead>
                  <tr>
                    <th>CUSTOMER NAME</th>
                    <th>TICKET TYPE</th>
                    <th>EXHIBITION</th>
                    <th>QTY</th>
                    <th>TOTAL PAID</th>
                    <th>STATUS</th>
                  </tr>
                </thead>
                <tbody>
                  {(bookings || []).map((booking, index) => (
                    <tr key={index} ref={(el) => (tableRowsRef.current[index] = el)}>
                      <td className="hm-table__name">{booking.user_name}</td>
                      <td>{booking.tier_name}</td>
                      <td>{booking.exhibitor_name}</td>
                      <td>{booking.quantity}</td>
                      <td className="hm-table__amount">EGP {booking.total_paid?.toLocaleString()}</td>
                      <td>
                        <span className={
                          booking.payment_status === 'PAID' ? 'hm-status hm-status--paid' :
                          booking.payment_status === 'PENDING' ? 'hm-status hm-status--pending' :
                          booking.payment_status === 'REFUNDED' ? 'hm-status hm-status--refunded' :
                          'hm-status'
                        }>
                          {booking.payment_status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </DashboardLayout>
    </>
  );
};

export default Home;